import { format, isSameDay } from 'date-fns';
import { CalendarIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useRef, useState, type ComponentPropsWithRef, type ReactElement } from 'react';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
    DialogTrigger,
} from '../containers/dialog.tsx';
import {
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverIconClose,
    PopoverTitle,
    PopoverTrigger,
} from '../containers/popover.tsx';
import { Button } from '../primitives/button.tsx';
import { Menu, MenuItem, MenuItemLabel, MenuItemTrailing } from '../primitives/menu.tsx';
import { ValueChip, ValueChipBody, ValueChipLabel, ValueChipRemove } from '../primitives/value-chip.tsx';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { Calendar, type CalendarProps, type DateRange } from './calendar.tsx';

export type { DateRange } from './calendar.tsx';

export type DatePickerContentProps = CalendarProps & {
    value?: Date;
    defaultValue?: Date;
    onValueChange?: (date: Date | undefined) => void;
    /** Called once the click is the whole answer — close the surface here. */
    onDismiss?: () => void;
};

/**
 * Fills a `Popover`'s body and asks to be dismissed on the click — one tap is
 * the whole answer. Use {@link DatePicker} unless you need a different opener.
 */
export function DatePickerContent({
    value,
    defaultValue,
    onValueChange,
    onDismiss,
    className,
    ...props
}: DatePickerContentProps): ReactElement {
    const [selected, setSelected] = useControllableState<Date | undefined>({
        value,
        defaultValue,
        onChange: onValueChange,
    });

    return (
        // Snug in the anchored card; the sheet keeps its side padding and little
        // else, since a full-width calendar needs the room.
        <PopoverBody className={cn('flex justify-center px-default py-1 sm:p-default', className)}>
            <Calendar
                {...props}
                mode="single"
                selected={selected}
                onSelect={(date) => {
                    // DayPicker toggles a selection off; read that tap as "yes,
                    // this one" instead. Clearing is the trigger's cross.
                    if (date) {
                        setSelected(date);
                    }
                    onDismiss?.();
                }}
            />
        </PopoverBody>
    );
}

export type DateRangePreset = {
    label: string;
    getValue: () => DateRange;
};

export type DateRangePickerContentProps = Omit<CalendarProps, 'numberOfMonths'> & {
    value?: DateRange;
    defaultValue?: DateRange;
    onValueChange?: (range: DateRange | undefined) => void;
    /** Called once the range is committed or abandoned — close the surface here. */
    onDismiss?: () => void;
    /** Omit for the calendar alone. The app owns the list: labels need translating. */
    presets?: DateRangePreset[];
    /** Copy for the row that opens the calendar, below the presets on touch. */
    customDatesLabel?: string;
    applyLabel?: string;
    cancelLabel?: string;
};

/** A range is only an answer once both ends are set — what Apply waits for. */
function isCompleteRange(range: DateRange | undefined): boolean {
    return !!range?.from && !!range.to;
}

function isSameRange(a: DateRange | undefined, b: DateRange | undefined): boolean {
    if (!a?.from || !a.to || !b?.from || !b.to) {
        return false;
    }
    return isSameDay(a.from, b.from) && isSameDay(a.to, b.to);
}

type PresetRowProps = {
    preset: DateRangePreset;
    /** Tick against what the row would produce: the draft, or the value on touch. */
    checkedAgainst: DateRange | undefined;
    onPick: (range: DateRange) => void;
};

/** One preset row, ticked when it is the range in play. */
function PresetRow({ preset, checkedAgainst, onPick }: PresetRowProps): ReactElement {
    return (
        <MenuItem
            label={preset.label}
            onClick={() => {
                onPick(preset.getValue());
            }}
        >
            <MenuItemLabel />
            <MenuItemTrailing>
                <CheckIcon
                    className={cn(
                        'size-4 text-brand',
                        !isSameRange(checkedAgainst, preset.getValue()) && 'invisible',
                    )}
                />
            </MenuItemTrailing>
        </MenuItem>
    );
}

type RangeLayoutProps = CalendarProps & {
    presets?: DateRangePreset[];
    applyLabel: string;
    cancelLabel: string;
    /** The committed value — what the trigger shows. */
    selected: DateRange | undefined;
    onCommit: (range: DateRange | undefined) => void;
    onDismiss: () => void;
};

/** Pointer: the rail sits beside the months, everything visible at once. */
function RangeRail({
    presets,
    applyLabel,
    cancelLabel,
    selected,
    onCommit,
    onDismiss,
    className,
    ...props
}: RangeLayoutProps): ReactElement {
    // The surface unmounts on close, so every open starts from the committed value.
    const [draft, setDraft] = useState<DateRange | undefined>(selected);

    return (
        <>
            <DialogBody className={cn('flex justify-center py-1', className)}>
                <div className="flex w-full items-start gap-default">
                    {!!presets?.length && (
                        // Menu is `w-full`; the rail sizes to its longest label.
                        <Menu className="-ml-cozy w-max shrink-0 p-0">
                            {presets.map((preset) => (
                                <PresetRow
                                    key={preset.label}
                                    preset={preset}
                                    checkedAgainst={draft}
                                    onPick={setDraft}
                                />
                            ))}
                        </Menu>
                    )}
                    <Calendar {...props} mode="range" selected={draft} onSelect={setDraft} />
                </div>
            </DialogBody>
            <DialogFooter>
                <Button variant="secondary" onClick={onDismiss}>
                    {cancelLabel}
                </Button>
                <Button
                    variant="primary"
                    disabled={!isCompleteRange(draft)}
                    onClick={() => {
                        onCommit(draft);
                    }}
                >
                    {applyLabel}
                </Button>
            </DialogFooter>
        </>
    );
}

type RangeStepsProps = RangeLayoutProps & {
    /** The row that steps into the calendar. Touch only — the rail has no such row. */
    customDatesLabel: string;
};

/** Touch: presets lead, the calendar waits behind the "custom dates" row. */
function RangeSteps({
    presets,
    customDatesLabel,
    applyLabel,
    cancelLabel,
    selected,
    onCommit,
    onDismiss,
    className,
    ...props
}: RangeStepsProps): ReactElement {
    const [showCalendar, setShowCalendar] = useState(false);
    const [draft, setDraft] = useState<DateRange | undefined>(selected);

    // First step: a preset is the whole answer, so it commits and needs no footer.
    if (presets?.length && !showCalendar) {
        return (
            // Centring would undo the row's outdent, and a full-width menu has
            // nothing to centre anyway.
            <DialogBody className={cn('flex py-1', className)}>
                <Menu className="-mx-cozy p-0">
                    {presets.map((preset) => (
                        <PresetRow
                            key={preset.label}
                            preset={preset}
                            checkedAgainst={selected}
                            onPick={onCommit}
                        />
                    ))}
                    <MenuItem
                        label={customDatesLabel}
                        onClick={() => {
                            setShowCalendar(true);
                        }}
                    >
                        <MenuItemLabel />
                        <MenuItemTrailing>
                            <ChevronRightIcon className="size-4" />
                        </MenuItemTrailing>
                    </MenuItem>
                </Menu>
            </DialogBody>
        );
    }

    return (
        <>
            <DialogBody className={cn('flex justify-center py-1', className)}>
                <Calendar {...props} mode="range" selected={draft} onSelect={setDraft} />
            </DialogBody>
            <DialogFooter>
                <Button
                    variant="secondary"
                    onClick={() => {
                        setDraft(selected);
                        // Cancel is the way back, so that step carries no back row.
                        if (presets?.length) {
                            setShowCalendar(false);
                            return;
                        }
                        onDismiss();
                    }}
                >
                    {cancelLabel}
                </Button>
                <Button
                    variant="primary"
                    disabled={!isCompleteRange(draft)}
                    onClick={() => {
                        onCommit(draft);
                    }}
                >
                    {applyLabel}
                </Button>
            </DialogFooter>
        </>
    );
}

/**
 * Fills a `Dialog`'s body and pinned footer — {@link RangeRail} on a pointer,
 * {@link RangeSteps} on touch. Days edit a draft the footer commits, so half a
 * range never escapes as a value. Use {@link DateRangePicker} for the usual opener.
 */
export function DateRangePickerContent({
    value,
    defaultValue,
    onValueChange,
    onDismiss,
    presets,
    customDatesLabel = 'Custom dates',
    applyLabel = 'Apply',
    cancelLabel = 'Cancel',
    ...props
}: DateRangePickerContentProps): ReactElement {
    const touch = !useBreakpoint('sm');
    // Two months need room to sit beside the preset rail. Below `md` the pair
    // would crush them all, so a single month steps through instead.
    const numberOfMonths = useBreakpoint('md') ? 2 : 1;
    const [selected, setSelected] = useControllableState<DateRange | undefined>({
        value,
        defaultValue,
        onChange: onValueChange,
    });

    const layout = {
        ...props,
        numberOfMonths,
        presets,
        applyLabel,
        cancelLabel,
        selected,
        onCommit: (range: DateRange | undefined) => {
            setSelected(range);
            onDismiss?.();
        },
        onDismiss: () => {
            onDismiss?.();
        },
    };

    return touch ? <RangeSteps {...layout} customDatesLabel={customDatesLabel} /> : <RangeRail {...layout} />;
}

/** Clearing needs wording for the cross, so the two arrive together or not at all. */
type ClearControl = { onClear?: never; clearLabel?: never } | { onClear: () => void; clearLabel: string };

export type DateTriggerProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'value'> & {
    /** The formatted date, or nothing when the field is empty. */
    value?: string;
    /** Opener copy for the empty state. */
    placeholder: string;
} & ClearControl;

/**
 * Dashed while empty, a plain value chip once set — no icon, since a date isn't
 * a system entity. One element either way, so `PopoverTrigger` can render it.
 */
export function DateTrigger(props: DateTriggerProps): ReactElement {
    const { value, placeholder, onClear, clearLabel, className, ...restProps } = props;

    if (!value) {
        return (
            <ValueChip variant="suggestion" label={placeholder} className={className}>
                <ValueChipBody {...restProps}>
                    <CalendarIcon />
                    <ValueChipLabel />
                </ValueChipBody>
            </ValueChip>
        );
    }

    return (
        <ValueChip label={value} className={className}>
            <ValueChipBody {...restProps}>
                <ValueChipLabel />
            </ValueChipBody>
            {props.onClear === undefined ? undefined : (
                <ValueChipRemove label={props.clearLabel} onClick={props.onClear} />
            )}
        </ValueChip>
    );
}

export type DateRangeTriggerProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'value'> & {
    /** The formatted range, or nothing when the field is empty. */
    value?: string;
    placeholder: string;
};

/**
 * Range trigger: a filter pill, not a chip — a range reads as a standing filter,
 * so it carries no cross. Clearing happens through the presets inside.
 */
export function DateRangeTrigger({
    value,
    placeholder,
    className,
    ...props
}: DateRangeTriggerProps): ReactElement {
    return (
        <button
            type="button"
            {...props}
            className={cn(
                'flex w-fit shrink-0 cursor-pointer items-center gap-tight rounded-full border border-line',
                'pr-cozy pl-default text-ui-base text-content',
                'transition-colors duration-state ease-state select-none',
                'hover:bg-container-hover active:translate-px',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
                'state-disabled:pointer-events-none state-disabled:opacity-disabled',
                'h-interactive',
                className,
            )}
        >
            <CalendarIcon className="size-4 shrink-0 text-content-weaker" />
            <span className={cn('whitespace-nowrap', !value && 'text-content-weaker')}>
                {value || placeholder}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 text-content-weaker" />
        </button>
    );
}

/** What both ready-made pickers need to open and label themselves. */
type FieldProps = {
    /** Heads the sheet and the large dialog; anchored popovers show no header. */
    title: string;
    /** Opener copy while the field is empty. */
    placeholder: string;
    /** `date-fns` pattern for the trigger's value, resolved with `locale`. */
    dateFormat?: string;
    /**
     * An opener of your own, in place of the built-in one — the surface, its
     * header and its focus stay the field's, as with any `render` in this
     * package. For a different *surface*, use the `*Content` parts instead.
     */
    render?: ReactElement;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
};

export type DatePickerProps = DatePickerContentProps &
    FieldProps & {
        /** Pass it to offer clearing; it names the built-in trigger's cross. */
        clearLabel?: string;
    };

/**
 * Single-date field: trigger, surface and calendar, ready for a form. Opens from
 * a {@link DateTrigger}; for any other opener use {@link DatePickerContent}.
 */
export function DatePicker({
    title,
    placeholder,
    dateFormat = 'PP',
    render,
    clearLabel,
    open,
    defaultOpen = false,
    onOpenChange,
    value,
    onValueChange,
    ...props
}: DatePickerProps): ReactElement {
    // The content dismisses the surface, so the field owns `open` either way.
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });
    const popupRef = useRef<HTMLDivElement>(null);
    const clearControl: ClearControl =
        clearLabel === undefined
            ? {}
            : {
                  clearLabel,
                  onClear: () => {
                      onValueChange?.(undefined);
                  },
              };

    return (
        <Popover open={isOpen} onOpenChange={setOpen}>
            <PopoverTrigger
                render={
                    render ?? (
                        <DateTrigger
                            value={value && format(value, dateFormat, { locale: props.locale })}
                            placeholder={placeholder}
                            {...clearControl}
                        />
                    )
                }
            />
            {/* Focus the card, not the day under the cursor. */}
            <PopoverContent ref={popupRef} initialFocus={popupRef} aria-label={title} className="sm:w-fit">
                {/* No hairline: a calendar never scrolls, and the header's
                    rule is the body's scroll telling you there is more. */}
                <PopoverHeader>
                    <PopoverTitle>{title}</PopoverTitle>
                    <PopoverIconClose className="ml-auto" />
                </PopoverHeader>
                <DatePickerContent
                    {...props}
                    value={value}
                    onValueChange={onValueChange}
                    onDismiss={() => {
                        setOpen(false);
                    }}
                />
            </PopoverContent>
        </Popover>
    );
}

export type DateRangePickerProps = DateRangePickerContentProps & FieldProps;

/**
 * Range field, ready for a form. Opens from a {@link DateRangeTrigger} into a
 * dialog, since two months beside a preset rail outgrow an anchored popover.
 * For any other opener use {@link DateRangePickerContent}.
 */
export function DateRangePicker({
    title,
    placeholder,
    dateFormat = 'PP',
    render,
    open,
    defaultOpen = false,
    onOpenChange,
    value,
    onValueChange,
    ...props
}: DateRangePickerProps): ReactElement {
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });
    const locale = props.locale;
    // A range that came from a preset keeps the preset's name: the dates are
    // re-derived on every load, so spelling them out dates the trigger.
    const preset = props.presets?.find((candidate) => isSameRange(value, candidate.getValue()));
    const text =
        preset?.label ??
        (value?.from && value.to
            ? `${format(value.from, dateFormat, { locale })} – ${format(value.to, dateFormat, { locale })}`
            : undefined);

    return (
        <Dialog open={isOpen} onOpenChange={setOpen}>
            <DialogTrigger render={render ?? <DateRangeTrigger value={text} placeholder={placeholder} />} />
            <DialogContent variant="picker" snapMode="contain" className="sm:max-w-fit">
                <DialogHeader className="p-0 px-5 pt-5 pb-2">
                    <DialogTitle>{title}</DialogTitle>
                    <DialogIconClose className="ml-auto" />
                </DialogHeader>
                <DateRangePickerContent
                    {...props}
                    value={value}
                    onValueChange={onValueChange}
                    onDismiss={() => {
                        setOpen(false);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}
